// Console view of Channels Light (#207). Shared mapping and RPCs live in @secops/shared.
// This module adds only the create-form target options (company + sites).

export {
  ARCHIVE_CHANNEL_RPC,
  CHANNELS_LOAD_FAILED,
  CHANNELS_MANAGE_PERMISSION,
  CHANNELS_UNAVAILABLE,
  CHANNEL_ANNOUNCE_ONLY_NOTE,
  CHANNEL_DM_UNAVAILABLE_NOTE,
  CHANNEL_GROUP_LABELS,
  CHANNEL_KINDS,
  CHANNEL_NO_POSTS,
  CHANNEL_POSTS_LOAD_FAILED,
  CREATE_CHANNEL_RPC,
  EMPTY_CHANNEL_ANNOUNCEMENT_DRAFT,
  EMPTY_CHANNEL_DRAFT,
  GET_CHANNEL_RPC,
  KNOWN_CHANNEL_FAILURES,
  LIST_CHANNELS_RPC,
  LIST_CHANNEL_MESSAGES_RPC,
  LIST_CHANNEL_POSTS_RPC,
  MARK_CHANNEL_READ_RPC,
  POST_CHANNEL_ANNOUNCEMENT_RPC,
  POST_CHANNEL_MESSAGE_RPC,
  SAFE_CHANNEL_FAILURE,
  archiveChannel,
  channelSubtitle,
  channelUnreadBadge,
  createChannel,
  fetchChannelMessages,
  fetchChannelPosts,
  fetchChannels,
  groupChannels,
  isMissingChannelSurface,
  mapChannel,
  mapChannelMessage,
  mapChannelMessages,
  mapChannels,
  postChannelAnnouncement,
  postChannelMessage,
  sortChannelMessages,
  sortChannelPosts,
  validateChannelAnnouncement,
  validateChannelDraft,
} from "../../../../packages/shared/src/channels.ts";

export type {
  Channel,
  ChannelAnnouncementDraft,
  ChannelDraft,
  ChannelGroup,
  ChannelKind,
  ChannelMessage,
  ChannelPostsLoad,
  ChannelWriteOutcome,
  ChannelsLoad,
} from "../../../../packages/shared/src/channels.ts";

import type { ClientRow, ManagedSite } from "./clients-sites";
import { sitesForPicker } from "../lib/setup-site-options";
import type { ChannelKind } from "../../../../packages/shared/src/channels.ts";

export interface ChannelTargetOption {
  readonly kind: ChannelKind;
  readonly id: string | null;
  readonly label: string;
}

export const COMPANY_CHANNEL_TARGET: ChannelTargetOption = {
  kind: "account",
  id: null,
  label: "Company-wide",
};

/**
 * What a new channel can be pointed at: the whole account, or one active site. Shift groups are in
 * the contract and render when the server returns them, but the console has no shift-group
 * picker source yet, so creating one is not offered.
 */
export function channelTargetOptions(
  sites: readonly ManagedSite[],
  clients: readonly ClientRow[],
): ChannelTargetOption[] {
  const siteOptions = sitesForPicker([...sites], [...clients]).map((site) => ({
    kind: "site" as const,
    id: site.siteId,
    label: site.siteName,
  }));
  return [COMPANY_CHANNEL_TARGET, ...siteOptions];
}

export function encodeChannelTargetValue(option: ChannelTargetOption): string {
  return option.id === null ? option.kind : `${option.kind}:${option.id}`;
}

export function decodeChannelTargetValue(
  value: string,
  options: readonly ChannelTargetOption[],
): ChannelTargetOption {
  return options.find((option) => encodeChannelTargetValue(option) === value) ?? COMPANY_CHANNEL_TARGET;
}
